import { IHomeOwner, IServiceRequest } from 'src/services/client-types';
import moment from 'moment/moment';
import {
  CSVHeaderConfig,
  CSVHeadersConfig,
  GeneratedCSVInfo,
  ITransformedServiceRequest,
  ITransformedServiceRequestCSVResponse,
  UpdatedHomeownerData,
} from 'src/pages/SharedPageCustomHooks/UseDownloadCSV/types';

export const downloadCSV = (
  csvData: string,
  filename: string = 'export.csv'
): void => {
  // Add BOM for proper UTF-8 handling in Excel.
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvData], {
    type: 'text/csv;charset=utf-8;',
  });

  const url: string = URL.createObjectURL(blob);
  const link: HTMLAnchorElement = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object.
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

const processAttachments = (attachment: string): string => {
  if (!attachment?.length) return '';

  try {
    const parsed: any = JSON.parse(attachment);
    if (!Array.isArray(parsed) || parsed.length === 0) return '';

    const cleaned: any[] = parsed
      .filter((item: any) => item != null && item !== 'undefined')
      .filter((item: any) => !item.includes('base64'));

    return JSON.stringify(cleaned);
  } catch {
    return '';
  }
};

const formatDatesForCSV = (item: IServiceRequest): IServiceRequest => {
  const dateFields = [
    'createdDateSt',
    'scheduledAt',
    'lastUpdatedst',
    'approvedDatest',
    'underReviewDatest',
    'assignedDatest',
    'inProgressDatest',
    'enRouteDatest',
    'onHoldDatest',
    'declinedDatest',
    'scheduleDatest',
    'resolvedDatest',
    'closedDatest',
    'oneYearWarrantyDatest',
  ];

  const formatted = { ...item };

  dateFields.forEach((field: string) => {
    if (formatted[field]) {
      formatted[field] = moment(formatted[field]).format('MM-DD-YYYY');
    }
  });

  return formatted;
};

const extractHomeownerData = (homeOwners: IHomeOwner): UpdatedHomeownerData => {
  if (!homeOwners?.id) {
    return {
      homeOwnerName: '',
      homeOwnerEmail: '',
      homeOwnerContactInfo: '',
    };
  }

  console.log('Homeowner data:', {
    firstname: homeOwners.firstname,
    lastname: homeOwners.lastname,
    fullName:
      `${homeOwners.firstname || ''} ${homeOwners.lastname || ''}`.trim(),
  });

  return {
    homeOwnerName:
      `${homeOwners.firstname || ''} ${homeOwners.lastname || ''}`.trim(),
    homeOwnerEmail: homeOwners.email || '',
    homeOwnerContactInfo: homeOwners.phone || '',
  };
};

const transformServiceRequestsForCSV = (
  apiData: IServiceRequest[]
): ITransformedServiceRequest[] => {
  return apiData.map((item: IServiceRequest) => {
    const withFormattedDates: IServiceRequest = formatDatesForCSV(item);
    const processedAttachment: string = processAttachments(item.attachment);
    const homeownerData: UpdatedHomeownerData = extractHomeownerData(
      item.homeOwners
    );

    return {
      ...withFormattedDates,
      attachment: processedAttachment ? JSON.parse(processedAttachment) : null,
      ...homeownerData,
    };
  });
};

const escapeCsvValue = (value: string | ITransformedServiceRequest): string => {
  if (value === null || value === undefined) {
    return '';
  }

  // Handle arrays (like attachments).
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '';
    }

    // Use recursion to join array elements with semicolons, then escape the whole thing.
    const arrayString: string = value.join('; ');
    return escapeCsvValue(arrayString);
  }

  // Handle objects.
  if (typeof value === 'object') {
    return ''; // Don't try to stringify complex objects.
  }

  // Convert to string.
  let stringValue: string = String(value).trim();

  // Handle empty strings.
  if (stringValue === '') {
    return '';
  }

  // Check if quoting is needed.
  const needsQuoting: boolean =
    stringValue.includes(',') ||
    stringValue.includes('"') ||
    stringValue.includes('\n') ||
    stringValue.includes('\r') ||
    stringValue.startsWith(' ') ||
    stringValue.endsWith(' ');

  if (needsQuoting) {
    // Escape existing quotes by doubling them.
    stringValue = stringValue.replace(/"/g, '""');
    // Wrap in quotes.
    stringValue = `"${stringValue}"`;
  }

  return stringValue;
};

export const getCSVLabels = (config: CSVHeadersConfig): string[] => {
  return config.map((header: CSVHeaderConfig) => header.label);
};

/* Extracts CSV column values from a service request object using property paths.
 * Each service request becomes a CSV row, and this function gets the value for each column.
 *
 * Simple properties: 'propertyName' → serviceRequest.propertyName
 * Nested properties: 'area.name' → serviceRequest.area.name
 * Missing paths return empty string to maintain CSV structure */
const getNestedValue = (
  obj: ITransformedServiceRequest,
  path: string
): ITransformedServiceRequest | string => {
  if (!path) return '';
  if (!obj) return '';

  // Handle simple property access (no dots).
  if (!path.includes('.')) {
    const result = obj[path];
    return result === undefined ? '' : result;
  }

  // Handle nested properties (like area.name).
  const keys: string[] = path.split('.');
  let current: ITransformedServiceRequest = obj;

  for (const key of keys) {
    if (current === null || current === undefined) {
      return '';
    }

    current = current[key];
  }

  return current === undefined ? '' : current;
};

const generateCSVString = (
  transformedServiceRequestsForCSV: ITransformedServiceRequestCSVResponse,
  csvHeadersConfig: CSVHeaderConfig[]
): string => {
  if (
    !transformedServiceRequestsForCSV ||
    transformedServiceRequestsForCSV.serviceRequestList.length === 0
  ) {
    return getCSVLabels(csvHeadersConfig).join(','); // Return just headers if no data is found.
  }

  const csvRows: string[] = [];
  const csvHeaders: string[] = csvHeadersConfig.map(
    (header: CSVHeaderConfig) => {
      return header.label; // Position 0: "Property Name", Position 1: "Suite Number", etc.
    }
  );
  const escapedHeaders: string[] = csvHeaders.map((header: string) =>
    escapeCsvValue(header)
  );
  const headerRow: string = escapedHeaders.join(',');

  csvRows.push(headerRow);

  // Process each data row of the API data to convert to a CSV.
  transformedServiceRequestsForCSV.serviceRequestList.forEach(
    (row: ITransformedServiceRequest, rowIdx: number) => {
      try {
        const values: string[] = csvHeadersConfig.map(
          (header: CSVHeaderConfig) => {
            // Extract value using header.key - the csvHeadersConfig array order determines CSV column order here.
            const rawValues: string | ITransformedServiceRequest =
              getNestedValue(row, header.key);

            return escapeCsvValue(rawValues);
          }
        );

        csvRows.push(values.join(','));
      } catch (error) {
        console.error(`Failed to process row ${rowIdx}:`, error);
        console.error('Problematic row data:', row);
        throw new Error(
          `CSV generation failed at row ${rowIdx}: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      }
    }
  );

  return csvRows.join('\r\n');
};

export const generateCSVFromAPIResponse = (
  transformedServiceRequestsForCSV: ITransformedServiceRequestCSVResponse,
  csvHeadersConfig: CSVHeadersConfig
): GeneratedCSVInfo => {
  try {
    const filename: string = 'service-requests.csv';

    const csv: string = generateCSVString(
      transformedServiceRequestsForCSV,
      csvHeadersConfig
    );

    return { csv, filename };
  } catch (error) {
    console.error('CSV generation failed:', error);
    throw new Error('Failed to generate CSV file');
  }
};

export {
  escapeCsvValue,
  processAttachments,
  formatDatesForCSV,
  extractHomeownerData,
  transformServiceRequestsForCSV,
  generateCSVString,
  getNestedValue,
};
